import React, { Fragment, PureComponent } from 'react';

import styled from 'styled-components';

import Box from '../../atoms/Box/Box';
import Button from '../../atoms/Button/Button';
import { Col, Row } from '../../atoms/Grid';
import Text from '../../atoms/Text/Text';
import SubscriptionInfo from '../../molecules/SubscriptionInfo/SubscriptionInfo';

import { localizedMoment } from '@utils/localizedMoment';

import { BillingHistory } from '@shared/interfaces/billing-history';
import BillingHistoryPopup from '../BillingHistoryPopup';
import { Color } from '@shared/enums/button-color';
import { DATE_FORMAT } from '@shared/markup/constants';
import { I18nTranslation } from '@shared/interfaces/i18n';
import { Size } from '@shared/enums/button-size';
import { withTranslation } from 'i18n';

const BoxLink = styled(Box)`
  cursor: pointer;
`;

type PurchasedSubscriptionProps = {
  appName: string;
  purchasedDate: string | Date;
  expireDate: string | Date;
  licenseName: string;
  licenseImage: string;
  storeName: string;
  onReactive: () => void;
  onComparePlans: () => void;
  fromOurStore: boolean;
  fromOfficialStore: boolean;
  hasActive: boolean;
  hasExpired: boolean;
  hasBillingRetry?: boolean;
  billingHistory?: BillingHistory;
  price?: string;
  hasOriginalSubscription: boolean;
  hasAlipayRestrictions: boolean;
  hideForB2C: boolean;
  t: I18nTranslation;
};

class PurchasedSubscription extends PureComponent<PurchasedSubscriptionProps> {
  state = {
    showBillingHistory: false,
  };

  t = (key, options?) => this.props.t(`subs.${key}`, options);

  handleOpenBillingHistoryPopup = () => {
    this.setState({
      showBillingHistory: true,
    });
  };

  handleCloseBillingHistoryPopup = () => {
    this.setState({
      showBillingHistory: false,
    });
  };

  renderBillingInfo = () => {
    const {
      expireDate,
      storeName,
      fromOfficialStore,
      hasActive,
      fromOurStore,
      hasAlipayRestrictions,
      hideForB2C,
    } = this.props;

    const endDateFormat = localizedMoment(expireDate);
    const dateMessage = this.t(
      hasActive && !hasAlipayRestrictions ? 'nextBilling' : 'expires',
      {
        date: endDateFormat,
      }
    );

    return (
      <Fragment>
        <Text
          component="div"
          size={1.125}
          color="cornflowerBlue"
          lineHeight={1.5}
        >
          {dateMessage}
        </Text>
        {fromOurStore && !hasActive && !hideForB2C && (
          <Text
            component="div"
            size={1.125}
            color="monza"
            weight={600}
            lineHeight={1.5}
          >
            {this.t('reactivateText')}
          </Text>
        )}

        {fromOurStore && (
          <BoxLink onClick={this.handleOpenBillingHistoryPopup}>
            <BillingText size={1.125} lineHeight={2} color="abyss">
              {this.t('viewBilling')}
            </BillingText>
          </BoxLink>
        )}

        {fromOfficialStore && (
          <Text component="div" size={1.125} color="osloGray" lineHeight={1.5}>
            {storeName}
          </Text>
        )}
      </Fragment>
    );
  };

  renderBillingRetryInfo = () => {
    const { storeName, fromOfficialStore, fromOurStore } = this.props;

    return (
      <Fragment>
        <Text
          component="div"
          size={1.125}
          color="monza"
          weight={600}
          lineHeight={1.5}
        >
          {this.t('rebilling')}
        </Text>

        {fromOurStore && (
          <BoxLink onClick={this.handleOpenBillingHistoryPopup}>
            <Text size={1.125} lineHeight="normal" color="abyss">
              {this.t('viewBilling')}
            </Text>
          </BoxLink>
        )}

        {fromOfficialStore && (
          <Text component="div" size={1.125} color="osloGray" lineHeight={1.5}>
            {storeName}
          </Text>
        )}
      </Fragment>
    );
  };

  renderButtons = () => {
    const { hasOriginalSubscription, onReactive, onComparePlans } = this.props;

    return (
      <Box>
        <Row gutter={0.25}>
          {hasOriginalSubscription && (
            <Col xs={12} sm={6}>
              <Box smMb={0.75}>
                <Button
                  onClick={onReactive}
                  colorType={Color.FullGreen}
                  sizeType={Size.Three}
                >
                  {this.t('reactivate')}
                </Button>
              </Box>
            </Col>
          )}
          <Col xs={12} sm={6}>
            <Button
              onClick={onComparePlans}
              colorType={Color.FullTransparentBorderGreen}
              sizeType={Size.Three}
            >
              {this.t('compare')}
            </Button>
          </Col>
        </Row>
      </Box>
    );
  };

  render() {
    const {
      appName,
      purchasedDate,
      licenseImage,
      licenseName,
      hasExpired,
      hasBillingRetry,
      billingHistory,
      price,
      hasOriginalSubscription,
    } = this.props;

    let billingMessage = this.renderBillingInfo();

    if (hasExpired) {
      billingMessage = this.renderButtons();
    }

    if (hasBillingRetry) {
      billingMessage = this.renderBillingRetryInfo();
    }

    return (
      <Fragment>
        <Box>
          <Row>
            <Col xs={12} sm={6}>
              <SubscriptionInfo
                hasExpired={hasExpired}
                appName={appName}
                licenseImage={licenseImage}
                licenseName={licenseName}
                purchasedDate={purchasedDate}
                format={DATE_FORMAT}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Box smMt={1.625}>
                {hasOriginalSubscription && (
                  <Text
                    style={{ marginBottom: 0.75 }}
                    component="div"
                    smSize={1.375}
                    size={1.5}
                    weight="bold"
                    color="cornflowerBlue"
                    lineHeight={1.5}
                  >
                    {price}
                  </Text>
                )}
                {billingMessage}
              </Box>
            </Col>
          </Row>
        </Box>

        {this.state.showBillingHistory && (
          <BillingHistoryPopup
            billingHistory={billingHistory}
            licenseName={licenseName}
            onClose={this.handleCloseBillingHistoryPopup}
          />
        )}
      </Fragment>
    );
  }
}

const BillingText = styled(Text)`
  text-transform: uppercase;
  text-decoration: underline;
`;

export default withTranslation('account')(PurchasedSubscription);
